import { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { HashOutline, RefreshOutline, StarOutline, TimerOutline } from '@lyearn/icons';
import { Stack, Typography } from '@lyearn/ui';
import QuizSettingsEntity from '@/modules/corporate/modules/quiz/entity/QuizSettings';
import { QuestionsProps } from '@/modules/corporate/modules/quiz/views/QuizDetails/components/Questions/types';
import { getQuizTimerDuration } from '@/modules/corporate/modules/quiz/views/QuizDetails/components/QuizDetailsHero/utils';
import { QuizDetailsContentFragment } from '@/modules/corporate/modules/quiz/views/QuizDetails/graphql/fragments/generated/QuizDetailsContent';
import { QuizDetailsPerformanceFragment } from '@/modules/corporate/modules/quiz/views/QuizDetails/graphql/fragments/generated/QuizDetailsPerformance';
import getQuizModifiedAttempts from '@/modules/corporate/modules/quiz/views/QuizDetails/utils/getQuizModifiedAttempts';

type QuizInfoProps = {
  quiz: QuizDetailsContentFragment;
  quizPerformance?: QuizDetailsPerformanceFragment;
};

function InfoPill(props: QuestionsProps) {
  const { Icon, text, textColor = 'text-text-primary' } = props;

  return (
    <Stack
      alignItems="center"
      className="h-32 w-max rounded-6 border border-interactive-secondary ps-8 pe-10">
      <Icon className="h-16 w-16 me-6" fillColorToken="icon-primary" />
      <Typography className="whitespace-pre" color={textColor} variant="body-short-02">
        {text}
      </Typography>
    </Stack>
  );
}

const QuizInfo: React.FC<QuizInfoProps> = ({ quiz, quizPerformance }) => {
  const { t } = useTranslation();

  const maxAttempts = QuizSettingsEntity.getAllowedAttempts(quiz.settings);
  const attemptsCount = useMemo(
    () => getQuizModifiedAttempts(quizPerformance).length,
    [quizPerformance],
  );

  return (
    <Stack className="mt-16 xs:mt-12 flex-wrap gap-12">
      <InfoPill
        Icon={HashOutline}
        text={t('QuizPlayer:Questions', {
          count: quiz.quizQuestionsConfig?.questionCount || 0,
        })}
        textColor="text-text-secondary"
      />
      <InfoPill
        Icon={StarOutline}
        text={t('QuizPlayer:MaximumScoreInfo', {
          score: quiz.maxScore,
        })}
        textColor="text-text-secondary"
      />
      {quiz.settings?.timeLimit ? (
        <InfoPill
          Icon={TimerOutline}
          text={getQuizTimerDuration(t, quiz)}
          textColor="text-text-secondary"
        />
      ) : null}
      <InfoPill
        Icon={RefreshOutline}
        text={t('QuizPlayer:AttemptsInfo', {
          count: attemptsCount,
          max: maxAttempts,
        })}
        textColor="text-text-secondary"
      />
    </Stack>
  );
};

export default QuizInfo;
